// src/component/page/arena/ArenaPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  Alert,
  Snackbar,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  LinearProgress,
  Chip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SportsMmaRoundedIcon from "@mui/icons-material/SportsMmaRounded";
import LocalFireDepartmentRoundedIcon from "@mui/icons-material/LocalFireDepartmentRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";

/* =========================
   CONFIG
   ========================= */
const URL_MAIN =
  process.env.NODE_ENV === "production"
    ? window.location.origin
    : "http://localhost:3002";

const ENDPOINTS = {
  status: `${URL_MAIN}/api/arena/status`,
  leaderboard: `${URL_MAIN}/api/arena/leaderboard`,
  avatar: `${URL_MAIN}/api/arena/avatar`,
  queueStart: `${URL_MAIN}/api/arena/queue/start`,
  queueResolve: `${URL_MAIN}/api/arena/queue/resolve`,
  feed: `${URL_MAIN}/api/arena/feed`, // feed global tickets / actividad
  battleFeed: `${URL_MAIN}/api/arena/battle-feed`, // feed global de batallas / economía
  // === NUEVO: feed de reinicio / payouts semanales ===
  resetFeed: `${URL_MAIN}/api/arena/reset-feed`,
};

// === NUEVO: símbolo VIP configurable ===
const VIP_SYMBOL = process.env.REACT_APP_ARENA_VIP_SYMBOL || "VIPHUESO";

// === NUEVO: símbolo/decimales SWAP configurables ===
const SWAP_SYMBOL = process.env.REACT_APP_ARENA_SWAP_SYMBOL || "SWAP.HIVE";
const SWAP_DECIMALS = Number(process.env.REACT_APP_ARENA_SWAP_DECIMALS || "2");

// formateo genérico con decimales (para tokens tipo SWAP.HIVE)
const fmtToken = (n, dec = 8) => {
  const v = Number(n || 0);
  if (!isFinite(v)) return "0";
  if (dec === 0) return new Intl.NumberFormat().format(Math.floor(v));
  return v.toFixed(dec);
};

// SPA route to Burn (Album tab 6)
const BURN_PATH = "/game/album?page=6";

/* ===== Bone Power Table ===== */
const BONE_VALUES = {
  general: { C: 200, R: 600, Z: 1400, E: 2400, A: 3000, L: 5000 },
  especial: { C: 400, R: 1200, Z: 2800, E: 4800, A: 6000, L: 10000 },
};
const RAREZAS = ["C", "R", "Z", "E", "A", "L"];

const fmt = (n) => new Intl.NumberFormat().format(Number(n || 0));
const fmt3 = (n) => Number(n || 0).toFixed(3);

const fmtVip = (x) => {
  const v = Number(x || 0);
  if (v === 0) return "0";
  if (v >= 1) return fmt(v);
  return fmt3(v);
};

/* === Helpers === */
function getCookie(name) {
  try {
    return document.cookie
      .split("; ")
      .find((r) => r.startsWith(`${name}=`))
      ?.split("=")[1];
  } catch {
    return undefined;
  }
}
function getAuthHeaders() {
  const t =
    getCookie("token_bdbg") ||
    getCookie("token") ||
    localStorage.getItem("token_bdbg") ||
    localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}`, "x-token": t } : {};
}
async function fetchJson(url, options = {}) {
  const res = await fetch(url, options);
  let data = null;
  try {
    data = await res.json();
  } catch {}
  if (!res.ok) {
    const msg = data?.msg || data?.error || `HTTP ${res.status}`;
    const e = new Error(msg);
    e.response = { status: res.status, data };
    throw e;
  }
  return data ?? {};
}

// Razones que pertenecen al log de reset / payout (no al {tr("Arena Activity", "Actividad de la Arena")} normal)
const RESET_REASONS = [
  "SEASON_RESET",
  "RESET_SEASON",
  "RESET_SKIPPED",
  "POOL_PAYOUT_HUESO",
  "POOL_PAYOUT",
  "VIP_PAYOUT",
  "SWAP_PAYOUT",
  "TICKETS_PAYOUT",
];




/* === Avatars (Cloudinary) === */
const AVATARS = [
  {
    id: "av1",
    name: "Avatar-1",
    img: "https://res.cloudinary.com/dekoxefky/image/upload/v1761103436/BDB1_dwnssn.png",
  },
  {
    id: "av2",
    name: "Avatar-2",
    img: "https://res.cloudinary.com/dekoxefky/image/upload/v1761103908/BDB2_slr2pj.png",
  },
  {
    id: "av3",
    name: "Avatar-3",
    img: "https://res.cloudinary.com/dekoxefky/image/upload/v1761103909/BDB3_ejwzgm.png",
  },
];

// fallback avatar helper for leaderboard rows
function getAvatarForRow(row) {
  if (row?.avatar && row.avatar.img) {
    return {
      img: row.avatar.img,
      name: row.avatar.name || row.nombre || "avatar",
    };
  }
  if (row?.avatar && row.avatar.id) {
    const local = AVATARS.find((a) => a.id === row.avatar.id);
    if (local) {
      return { img: local.img, name: local.name || row.nombre || "avatar" };
    }
  }
  return {
    img: AVATARS[0].img,
    name: AVATARS[0].name,
  };
}

// PP a mostrar en leaderboard
function getRowPP(row) {
  if (typeof row?.pp === "number") return row.pp;
  if (typeof row?.ppActivo === "number") return row.ppActivo;
  if (typeof row?.power === "number") return row.power;
  if (typeof row?.preview?.pp === "number") return row.preview.pp;
  if (typeof row?.estimatedPower?.total === "number") return row.estimatedPower.total;
  return 0;
}

// SWAP preview helper para el leaderboard (flexible según responda el backend)
// SWAP preview helper (lee raíz y variantes) — admite decimales/símbolo dinámicos
function getRowSwapPreview(row, dec = SWAP_DECIMALS, sym = SWAP_SYMBOL) {
  try {
    // 0) raíz: row.swap === number (formato backend actual)
    if (typeof row?.swap === "number") {
      return fmtToken(row.swap, dec);
    }

    // 1) row.preview.swap === number
    const p = row?.preview || {};
    if (typeof p.swap === "number") {
      return fmtToken(p.swap, dec);
    }

    // 2) row.preview.swap.amount (+ opcional .decimals)
    if (p.swap && typeof p.swap.amount === "number") {
      return fmtToken(p.swap.amount, Number(p.swap.decimals ?? dec));
    }

    // 3) row.preview.swap[sym] === number
    if (p.swap && typeof p.swap[sym] === "number") {
      return fmtToken(p.swap[sym], dec);
    }

    // 4) row.preview.swapAmount === number
    if (typeof p.swapAmount === "number") {
      return fmtToken(p.swapAmount, dec);
    }
  } catch {}

  return fmtToken(0, dec);
}

function pickAvatarForUser(u) {
  if (u?.avatar?.img) {
    return { img: u.avatar.img, name: u.avatar.name || u.nombre || "avatar" };
  }
  if (u?.avatar?.id) {
    const local = AVATARS.find((a) => a.id === u.avatar.id);
    if (local) return { img: local.img, name: local.name || u.nombre || "avatar" };
  }
  return { img: AVATARS[0].img, name: u?.nombre || "Player" };
}

/* === Battle animation (video con sonido) === */
const BATTLE_ANIM =
  "https://res.cloudinary.com/dekoxefky/video/upload/v1761188757/Mi_video_1_pyhe4b.mp4";

/* === Ticket GIF (pedido) === */
const TICKET_GIF =
  "https://www.bigdogbonegame.com/static/media/ticket-stake-brillo.ecb5844af3e158141cef.gif";

/* === Porcentaje 50% → pool / 50% → burn (si backend no envía poolPercent) === */
const DEFAULT_POOL_PERCENT = 0.5;

// Normaliza el payload del reset-feed para tener SIEMPRE un array plano de eventos
// Normaliza el payload del reset-feed para tener SIEMPRE un array de eventos
function normalizeResetFeedPayload(d) {
  if (!d) return [];

  const raw = d.feed ?? d;

  // ✅ Si el backend ya manda un array plano de eventos, úsalo tal cual
  if (Array.isArray(raw)) {
    return raw;
  }

  // 🔙 Por compatibilidad con formatos viejos tipo { _legacy:[], "2025-W48":[...] }
  const out = [];

  const pushFromStore = (store) => {
    if (!store || typeof store !== "object") return;

    for (const key of Object.keys(store)) {
      if (key === "_legacy" || key === "season" || key === "seasonKey") continue;
      const arr = store[key];
      if (Array.isArray(arr)) {
        for (const ev of arr) {
          if (!ev) continue;
          out.push({
            ...ev,
            seasonKey: ev.seasonKey || ev.season || key,
          });
        }
      }
    }

    if (Array.isArray(store._legacy)) {
      for (const ev of store._legacy) {
        if (!ev) continue;
        out.push({
          ...ev,
          seasonKey: ev.seasonKey || ev.season || store.season || "_legacy",
        });
      }
    }
  };

  if (raw && typeof raw === "object") {
    pushFromStore(raw);
  }

  // Si llegamos aquí desde formato viejo, ordenamos un poco
  return out.sort((a, b) => {
    const ta = a.ts ? new Date(a.ts).getTime() : 0;
    const tb = b.ts ? new Date(b.ts).getTime() : 0;
    return tb - ta;
  }).slice(0, 20);
}


/* =========================
   UI HELPERS
   ========================= */
const HELP_TOOLTIP_SX = {
  bgcolor: "rgba(5, 8, 18, 0.98)",
  color: "#fff",
  border: "1px solid rgba(255, 193, 7, 0.55)",
  fontSize: "0.78rem",
  lineHeight: 1.35,
  maxWidth: 290,
  p: 1.1,
};

function HelpTooltip({ title, children, placement = "top" }) {
  return (
    <Tooltip
      arrow
      placement={placement}
      enterTouchDelay={0}
      leaveTouchDelay={3200}
      title={title}
      componentsProps={{ tooltip: { sx: HELP_TOOLTIP_SX } }}
    >
      <Box component="span" sx={{ display: "inline-flex", maxWidth: "100%" }}>
        {children}
      </Box>
    </Tooltip>
  );
}

function SectionHeader({ icon, title, help, action = null }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1}
      sx={{ mb: 1.4 }}
    >
      <Stack direction="row" alignItems="center" spacing={0.8}>
        <Typography component="span" sx={{ fontSize: "1.15rem", lineHeight: 1 }}>
          {icon}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 900, letterSpacing: 0.2 }}>
          {title}
        </Typography>
        {help && (
          <HelpTooltip title={help}>
            <InfoOutlinedIcon sx={{ fontSize: 18, color: "#f9a825", cursor: "help" }} />
          </HelpTooltip>
        )}
      </Stack>
      {action}
    </Stack>
  );
}

function StatTile({ icon, label, value, suffix = "", help, accent = "#ffca28" }) {
  const tile = (
    <Box
      sx={{
        minHeight: 78,
        p: 1.15,
        borderRadius: 2.2,
        border: `1px solid ${accent}55`,
        background: `linear-gradient(145deg, ${accent}18, rgba(255,255,255,0.94))`,
        boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
        transition: "transform 140ms ease, box-shadow 140ms ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: `0 9px 24px ${accent}25`,
        },
      }}
    >
      <Stack direction="row" spacing={0.8} alignItems="center">
        <Typography sx={{ fontSize: "1.25rem", lineHeight: 1 }}>{icon}</Typography>
        <Typography variant="caption" sx={{ fontWeight: 800, opacity: 0.72 }}>
          {label}
        </Typography>
      </Stack>
      <Typography sx={{ mt: 0.45, fontSize: "1.05rem", fontWeight: 950 }}>
        {value}{suffix}
      </Typography>
    </Box>
  );

  return help ? <HelpTooltip title={help}>{tile}</HelpTooltip> : tile;
}

export default function ArenaPage() {
  const [language, setLanguage] = useState(() => {
    if (typeof window === "undefined") return "en";
    return window.localStorage?.getItem("bdb_language") === "es" ? "es" : "en";
  });

  const tr = (english, spanish) =>
    language === "es" ? spanish : english;

  const changeLanguage = (nextLanguage) => {
    const normalizedLanguage = nextLanguage === "es" ? "es" : "en";
    setLanguage(normalizedLanguage);

    if (typeof window !== "undefined") {
      window.localStorage?.setItem("bdb_language", normalizedLanguage);
      window.dispatchEvent(
        new CustomEvent("bdb-language-changed", {
          detail: { language: normalizedLanguage },
        })
      );
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleLanguageChange = (event) => {
      const nextLanguage = event?.detail?.language;
      if (nextLanguage === "en" || nextLanguage === "es") {
        setLanguage(nextLanguage);
      }
    };

    const handleStorageChange = (event) => {
      if (event.key === "bdb_language" && (event.newValue === "en" || event.newValue === "es")) {
        setLanguage(event.newValue);
      }
    };

    window.addEventListener("bdb-language-changed", handleLanguageChange);
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("bdb-language-changed", handleLanguageChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const refreshLockRef = useRef(false);
  const [energyCost, setEnergyCost] = useState(5);
  const [entryFee, setEntryFee] = useState(500);
  const [minPP, setMinPP] = useState(100);
  const [energia, setEnergia] = useState(0);
  const [hueso, setHueso] = useState(0);
  const [username, setUsername] = useState("");
  const [perfil, setPerfil] = useState({
    avatar: AVATARS[0],
    pp: 0,
    ppOriginal: 0,
    ppQuema: 0,
    mmr: 1000,
    wins: 0,
    losses: 0,
    streak: 0,
    tickets: 0,
    userId: null,
    nombre: "",
    battlesThisWeek: 0,
    activityTicketsThisWeek: 0,
  });
  const [pool, setPool] = useState(0);
  const [poolPercent, setPoolPercent] = useState(DEFAULT_POOL_PERCENT);
  const [season, setSeason] = useState("");
  const [queueSize, setQueueSize] = useState(0);
  const [snack, setSnack] = useState({
    open: false,
    msg: "",
    severity: "info",
  });

  const [leader, setLeader] = useState([]);
  const [vipWeekly, setVipWeekly] = useState(0);
  const [vipSymbol, setVipSymbol] = useState(VIP_SYMBOL);
  const [swapSymbol, setSwapSymbol] = useState(SWAP_SYMBOL);
  const [swapDecimals, setSwapDecimals] = useState(SWAP_DECIMALS);


  // Quemado (del backend)
  const [burnedWeek, setBurnedWeek] = useState(0);
  const [burnedTotal, setBurnedTotal] = useState(0);

  // If backend includes rarity counts
  const [userCounts, setUserCounts] = useState(null);

  // feed global Arena (tickets / actividad)
  const [arenaFeed, setArenaFeed] = useState([]); // [{ts,user,qty,razon,detalle,...}]

  // feed global de batallas / economía
  const [battleFeed, setBattleFeed] = useState([]); // [{ts,type,winner,players:[...],poolAdded,burnAdded}]

  // === NUEVO: feed de reinicio / payouts semanales
  const [resetFeed, setResetFeed] = useState([]); // [{ts,type|razon,seasonKey,huesoSent,ticketsSent,vipSent,detalle,...}]


    // 👇 {tr("Arena Activity", "Actividad de la Arena")}: solo cosas normales (wins, actividad, etc.) sin payouts/reset
  const arenaActivityFeed = useMemo(
    () => arenaFeed.filter((ev) => !RESET_REASONS.includes(ev.razon)),
    [arenaFeed]
  );

  // 👇 Eventos de payout/reset que hoy vienen en ArenaFeed y deben ir al log de reset
  const arenaResetFromFeed = useMemo(
    () =>
      arenaFeed
        .filter((ev) => RESET_REASONS.includes(ev.razon))
        .map((ev) => {
          const base = {
            ts: ev.ts,
            user: ev.user,
            razon: ev.razon,
            detalle: ev.detalle,
            seasonKey: ev.seasonKey,
          };

          // Mapeamos qty → campos del Season Log
          if (ev.razon === "POOL_PAYOUT_HUESO" || ev.razon === "POOL_PAYOUT") {
            return { ...base, huesoSent: Number(ev.qty || 0) };
          }
          if (ev.razon === "VIP_PAYOUT") {
            return { ...base, vipSent: Number(ev.qty || 0) };
          }
          if (ev.razon === "TICKETS_PAYOUT") {
            return { ...base, ticketsSent: Number(ev.qty || 0) };
          }
          if (ev.razon === "SWAP_PAYOUT") {
            return {
              ...base,
              tokens: [
                {
                  symbol: ev.symbol || swapSymbol,
                  amount: Number(ev.qty || 0),
                  decimals: swapDecimals,
                },
              ],
            };
          }

          // SEASON_RESET / RESET_SKIPPED etc.
          return base;
        }),
    [arenaFeed, swapSymbol, swapDecimals]
  );

  // 👇 Feed final que usa el panel "Season Reset / Payout Log"
  const mergedResetFeed = useMemo(() => {
    const raw = [...(resetFeed || []), ...arenaResetFromFeed];
    return raw
      .filter(Boolean)
      .sort((a, b) => Number(b.ts || 0) - Number(a.ts || 0))
      .slice(0, 50);
  }, [resetFeed, arenaResetFromFeed]);



  // progreso global para desbloquear el pool
  const [unlockProgress, setUnlockProgress] = useState({
    current: 0,
    goal: 0,
    unlocked: false,
  });

  // Battle modal / anim
  const [battleOpen, setBattleOpen] = useState(false);
  const [battlePhase, setBattlePhase] = useState("anim"); // "anim" | "result"
  const [battleCountdown, setCountdown] = useState(7);
  const [battleResult, setBattleResult] = useState(null);

  const [animStatusText, setAnimStatusText] = useState("SEARCHING");

  // Reward modal
  const [rewardOpen, setRewardOpen] = useState(false);
  const [rewardInfo, setRewardInfo] = useState(null);

  // Video controls
  const videoRef = useRef(null);

  // reward pendiente hasta que termine animación
  const pendingRewardRef = useRef(null);

  const [muted, setMuted] = useState(false);

  const ANIM_MS = 7000; // experiencia más rápida sin cambiar la lógica del backend

  const animTimerRef = useRef(null);
  const [animDone, setAnimDone] = useState(false);
  const [resultReady, setResultReady] = useState(false);
  const resultBufferRef = useRef(null);

  const notify = (msg, severity = "info") =>
    setSnack({ open: true, msg, severity });
  const closeSnack = () =>
    setSnack((s) => ({
      ...s,
      open: false,
    }));

  // ===== loaders =====
  const loadStatus = async ({ silent = false } = {}) => {
    try {
      const d = await fetchJson(ENDPOINTS.status, {
        headers: { ...getAuthHeaders() },
      });

      setEnergyCost(Number(d.energyCost ?? 2));
      setEntryFee(Number(d.entryFee ?? 10));
      setMinPP(Number(d.minPP ?? d.minPp ?? 100));
      setEnergia(Number(d.energia ?? 0));
      setHueso(Number(d.hueso ?? 0));
      setPool(Number(d.pool ?? 0));
      setSeason(d.season || "");
      setQueueSize(Number(d.queueSize ?? 0));

      setBurnedWeek(Number(d.burnedWeek ?? 0));
      setBurnedTotal(Number(d.burnedTotal ?? 0));

      if (d.unlockProgress) {
        setUnlockProgress({
          current: Number(d.unlockProgress.current || 0),
          goal: Number(d.unlockProgress.goal || 0),
          unlocked: Boolean(d.unlockProgress.unlocked),
        });
      }

      const uname = d?.perfil?.nombre || d?.user?.nombre || "";
      if (uname) setUsername(uname);

      if (d.perfil) {
        let avatar = d.perfil?.avatar;
        if (avatar?.id) {
          const local = AVATARS.find((a) => a.id === avatar.id);
          if (local) avatar = local;
        }
        setPerfil((p) => ({
          ...p,
          ...d.perfil,
          avatar: avatar?.img
            ? avatar
            : AVATARS.find((a) => a.id === avatar?.id) || AVATARS[0],
          battlesThisWeek: d.perfil.battlesThisWeek ?? p.battlesThisWeek,
          activityTicketsThisWeek:
            d.perfil.activityTicketsThisWeek ?? p.activityTicketsThisWeek,
        }));
      }

      if (d.userCounts) setUserCounts(d.userCounts);
      if (typeof d.poolPercent === "number") {
        setPoolPercent(Number(d.poolPercent));
      } else {
        setPoolPercent(DEFAULT_POOL_PERCENT);
      }
    } catch (e) {
      if (!silent) notify(e?.message || tr("Unauthorized in Arena", "No autorizado en la Arena"), "error");
      throw e;
    }
  };

  const loadLeaderboard = async () => {
    try {
      const d = await fetchJson(`${ENDPOINTS.leaderboard}?size=200`, {
        headers: { ...getAuthHeaders() },
      });
      if (Array.isArray(d?.data)) setLeader(d.data);

      if (typeof d?.vipWeekly === "number") setVipWeekly(Number(d.vipWeekly));
      if (typeof d?.vipSymbol === "string" && d.vipSymbol.trim()) {
        setVipSymbol(d.vipSymbol.trim());
      }
      if (typeof d?.pool === "number") setPool(Number(d.pool));
      if (typeof d?.burnedWeek === "number") setBurnedWeek(Number(d.burnedWeek));
      if (typeof d?.burnedTotal === "number")
        setBurnedTotal(Number(d.burnedTotal));

      // Backend puede mandar símbolo/decimales efectivos para SWAP
if (typeof d?.swapSymbol === "string" && d.swapSymbol.trim()) {
  setSwapSymbol(d.swapSymbol.trim());
}
if (Number.isFinite(d?.swapDecimals)) {
  setSwapDecimals(Number(d.swapDecimals));
}


      if (d.unlockProgress) {
        setUnlockProgress({
          current: Number(d.unlockProgress.current || 0),
          goal: Number(d.unlockProgress.goal || 0),
          unlocked: Boolean(d.unlockProgress.unlocked),
        });
      }
    } catch {
      /* silent */
    }
  };

  const loadFeed = async () => {
    try {
      const d = await fetchJson(ENDPOINTS.feed, {
        headers: { ...getAuthHeaders() },
      });
      if (Array.isArray(d?.feed)) {
        const sorted = [...d.feed].sort((a, b) => b.ts - a.ts).slice(0, 20);
        setArenaFeed(sorted);
      }
    } catch {
      /* silent */
    }
  };

  const loadBattleFeed = async () => {
    try {
      const d = await fetchJson(ENDPOINTS.battleFeed, {
        headers: { ...getAuthHeaders() },
      });
      if (Array.isArray(d?.feed)) {
        const sorted = [...d.feed].sort((a, b) => b.ts - a.ts).slice(0, 20);
        setBattleFeed(sorted);
      }
      if (typeof d?.pool === "number") setPool(Number(d.pool));
      if (typeof d?.burnedWeek === "number") setBurnedWeek(Number(d.burnedWeek));
      if (typeof d?.burnedTotal === "number")
        setBurnedTotal(Number(d.burnedTotal));
    } catch {
      /* silent */
    }
  };

    // === Feed de reinicio / payouts semanales ===
  const loadResetFeed = async () => {
    try {
      const d = await fetchJson(ENDPOINTS.resetFeed, {
        headers: { ...getAuthHeaders() },
      });
      const normalized = normalizeResetFeedPayload(d);
      setResetFeed(normalized);
    } catch {
      /* silent */
    }
  };

  // Una sola actualización paralela: evita cinco esperas consecutivas y bloqueos repetidos.
  const refreshAll = async ({ initial = false, showMessage = false } = {}) => {
    if (refreshLockRef.current) return;
    refreshLockRef.current = true;

    if (initial) setLoading(true);
    else setRefreshing(true);

    try {
      await Promise.allSettled([
        loadStatus({ silent: !initial }),
        loadLeaderboard(),
        loadFeed(),
        loadBattleFeed(),
        loadResetFeed(),
      ]);

      if (showMessage) notify(tr("Arena updated", "Arena actualizada"), "success");
    } finally {
      refreshLockRef.current = false;
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Carga inicial
  useEffect(() => {
    refreshAll({ initial: true });
  }, []);

  // Actualización automática rápida, solo cuando la pestaña está visible.
  useEffect(() => {
    const updateVisibleArena = () => {
      if (document.visibilityState === "visible") refreshAll();
    };

    const id = setInterval(updateVisibleArena, 12000);
    window.addEventListener("focus", updateVisibleArena);
    document.addEventListener("visibilitychange", updateVisibleArena);

    return () => {
      clearInterval(id);
      window.removeEventListener("focus", updateVisibleArena);
      document.removeEventListener("visibilitychange", updateVisibleArena);
    };
  }, []);

  /* =============== Avatar =============== */
  const setAvatar = async (avatarId) => {
    try {
      await fetchJson(ENDPOINTS.avatar, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ avatarId }),
      });
      const chosen = AVATARS.find((a) => a.id === avatarId) || AVATARS[0];
      setPerfil((p) => ({ ...p, avatar: chosen }));
      notify(tr("Avatar updated", "Avatar actualizado"), "success");
    } catch (e) {
      const chosen = AVATARS.find((a) => a.id === avatarId);
      if (chosen) setPerfil((p) => ({ ...p, avatar: chosen }));
      notify(e?.message || tr("Avatar change failed", "No se pudo cambiar el avatar"), "error");
    }
  };

  /* =============== Battle animation (countdown + fin garantizado) =============== */
  useEffect(() => {
    if (!battleOpen || battlePhase !== "anim") return;

    setAnimStatusText("SEARCHING");

    setAnimDone(false);
    setResultReady(false);
    resultBufferRef.current = null;

    const seconds = Math.ceil(ANIM_MS / 1000);
    setCountdown(seconds);

    const phaseTimer = setTimeout(() => {
      setAnimStatusText("FIGHTING");
    }, 3200);

    const tick = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(tick);
          return 0;
        }
        return c - 1;
      });
    }, 1000);

    if (animTimerRef.current) clearTimeout(animTimerRef.current);
    animTimerRef.current = setTimeout(() => {
      setAnimDone(true);
    }, ANIM_MS);

    return () => {
      clearTimeout(phaseTimer);
      clearInterval(tick);
      if (animTimerRef.current) clearTimeout(animTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [battleOpen, battlePhase, ANIM_MS]);

  // === MATCH RESOLVE
  useEffect(() => {
    const runResolve = async () => {
      try {
        const d = await fetchJson(ENDPOINTS.queueResolve, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          body: JSON.stringify({}),
        });

        let myPp = perfil.pp;
        let myMmr = perfil.mmr;
        let outcome = { title: tr("Result", "Resultado"), detail: null };

        const result = d?.result;
        if (result) {
          if (result.type === "pvp") {
            const me =
              result?.a?.userId &&
              perfil?.userId &&
              result.a.userId === perfil.userId
                ? result.a
                : result.b;

            const opp =
              me && result?.a?.userId === me.userId ? result.b : result.a;

            let before = myPp;
            let consumed = 0,
              refund = 0,
              final = myPp;
            if (me?.decay) {
              before = Number(me.before ?? myPp);
              consumed = Number(me.decay.consumed ?? 0);
              refund = Number(me.decay.refund ?? 0);
              final = Number(me.decay.final ?? myPp);
            }

            const meAvatar = pickAvatarForUser(me);
            const oppAvatar = pickAvatarForUser(opp);

            const iWon = result.winner === (me?.userId ?? perfil.userId);
            const winnerName = iWon
              ? me?.nombre || tr("You", "Tú")
              : opp?.nombre || tr("Opponent", "Oponente");

            myPp = final;
            myMmr = Number(me?.mmr || myMmr);

            outcome = {
              title: tr("PvP Battle", "Batalla PvP"),
              detail: {
                type: "pvp",
                rounds: result.rounds?.length ?? 3,
                won: iWon,
                winnerName,
                matchup: {
                  winnerId: result.winner,
                  me: {
                    id: me?.userId,
                    name: me?.nombre || tr("You", "Tú"),
                    avatarUrl: meAvatar.img,
                    mmr: Number(me?.mmr || myMmr),
                  },
                  opp: {
                    id: opp?.userId,
                    name: opp?.nombre || tr("Opponent", "Oponente"),
                    avatarUrl: oppAvatar.img,
                    mmr: Number(opp?.mmr || 0),
                  },
                },
                pp: {
                  before,
                  consumed,
                  refund,
                  final,
                  lost: Math.max(0, consumed - refund),
                },
              },
            };
          } else if (result.type === "bot") {
            let before = myPp;
            let consumed = 0,
              refund = 0,
              final = myPp;

            if (result?.player?.decay) {
              before = Number(result.player.before ?? myPp);
              consumed = Number(result.player.decay.consumed ?? 0);
              refund = Number(result.player.decay.refund ?? 0);
              final = Number(result.player.decay.final ?? myPp);
            }

            const me = result.player || { userId: perfil.userId, nombre: username };
            const meAvatar = pickAvatarForUser(me);

            myPp = final;
            myMmr = Number(result?.player?.mmr || myMmr);

            outcome = {
              title: tr("Battle vs BOT", "Batalla contra BOT"),
              detail: {
                type: "bot",
                rounds: result.rounds?.length ?? 3,
                won: result.winner === (perfil.userId ?? "BOT"),
                winnerName:
                  result.winner === (perfil.userId ?? "BOT")
                    ? username || tr("You", "Tú")
                    : "BOT",
                matchup: {
                  winnerId: result.winner,
                  me: {
                    id: me?.userId,
                    name: me?.nombre || tr("You", "Tú"),
                    avatarUrl: meAvatar.img,
                    mmr: Number(result?.player?.mmr || myMmr),
                  },
                  opp: {
                    id: "BOT",
                    name: result?.bot?.name || "BOT",
                    avatarUrl: AVATARS[1].img,
                    mmr: Number(result?.bot?.mmr || 0),
                  },
                },
                pp: {
                  before,
                  consumed,
                  refund,
                  final,
                  lost: Math.max(0, consumed - refund),
                },
              },
            };
          }
        }

        const reward = result?.reward;

        if (reward && outcome?.detail?.won) {
          pendingRewardRef.current = reward;
        } else {
          pendingRewardRef.current = null;
        }

        const updatedBattlesThisWeek =
          result?.player?.battlesThisWeek ??
          result?.a?.battlesThisWeek ??
          perfil.battlesThisWeek;

        const updatedActivityTicketsThisWeek =
          result?.player?.activityTicketsThisWeek ??
          result?.a?.activityTicketsThisWeek ??
          perfil.activityTicketsThisWeek;

        setPerfil((p) => ({
          ...p,
          pp: myPp,
          mmr: myMmr,
          battlesThisWeek: updatedBattlesThisWeek,
          activityTicketsThisWeek: updatedActivityTicketsThisWeek,
        }));

        await Promise.allSettled([
          loadLeaderboard(),
          loadFeed(),
          loadBattleFeed(),
          loadResetFeed(),
          loadStatus({ silent: true }),
        ]);

        // Mantiene sincronizado el PP visible en el NavBar y otras pantallas.
        window.dispatchEvent(new Event("actualizar-poder-especial"));
        window.dispatchEvent(new Event("actualizar-saldos"));

        resultBufferRef.current = outcome;
        setResultReady(true);
      } catch (e) {
        resultBufferRef.current = {
          title: "Error",
          detail: {
            won: false,
            rounds: 0,
            winnerName: "—",
            matchup: null,
            pp: {
              before: perfil.pp,
              consumed: 0,
              refund: 0,
              final: perfil.pp,
              lost: 0,
            },
          },
        };
        setResultReady(true);
        notify(e?.message || tr("Battle resolve failed", "No se pudo resolver la batalla"), "error");
      }
    };

    if (animDone && !resultReady) {
      runResolve();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animDone, resultReady]);

  useEffect(() => {
    if (animDone && resultReady && resultBufferRef.current) {
      setBattleResult(resultBufferRef.current);
      setBattlePhase("result");

      if (pendingRewardRef.current) {
        setRewardInfo(pendingRewardRef.current);
        setTimeout(() => setRewardOpen(true), 200);
        pendingRewardRef.current = null;
      }
    }
  }, [animDone, resultReady]);

  const tryPlayVideo = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = muted;
    v.play().catch(() => {});
  };

  const joinBattle = async () => {
    setBattleOpen(true);
    setBattlePhase("anim");
    setBattleResult(null);
    setRewardOpen(false);
    setRewardInfo(null);
    setMuted(false);

    setAnimDone(false);
    setResultReady(false);
    resultBufferRef.current = null;
    pendingRewardRef.current = null;

    setTimeout(tryPlayVideo, 50);

    try {
      await fetchJson(ENDPOINTS.queueStart, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ nftId: null }),
      });
    } catch (e) {
      setBattleOpen(false);
      notify(e?.message || tr("Could not enter queue", "No se pudo entrar a la cola"), "error");
      return;
    }
  };

  /* =============== Estimated power (optional) =============== */
  const estimatedPower = useMemo(() => {
    if (!userCounts) return null;
    const g = userCounts.general || {};
    const e = userCounts.especial || {};
    let totalG = 0,
      totalE = 0;
    for (const r of RAREZAS) {
      totalG += (g[r] || 0) * (BONE_VALUES.general[r] || 0);
    }
    for (const r of RAREZAS) {
      totalE += (e[r] || 0) * (BONE_VALUES.especial[r] || 0);
    }
    return { general: totalG, especial: totalE, total: totalG + totalE };
  }, [userCounts]);

  /* =========================
     UI
     ========================= */
  const bgStyle = {
    minHeight: "100%",
    width: "100%",
    position: "relative",
    isolation: "isolate",
    backgroundImage:
      "linear-gradient(180deg, rgba(3,7,18,0.38), rgba(3,7,18,0.82)), url(https://res.cloudinary.com/dpwv380jp/image/upload/v1754942090/Blue_And_Black_Modern_YouTube_Shorts_Outro_Mobile_Video_1_rmzcny.gif)",
    backgroundPosition: "center",
    backgroundSize: "cover",
    backgroundAttachment: { md: "fixed" },
    px: { xs: 1, sm: 1.5, md: 2 },
    py: { xs: 1.25, md: 2 },
  };

  const cardProps = {
    sx: {
      p: { xs: 1.5, sm: 2 },
      mb: 2,
      bgcolor: "rgba(255,255,255,0.95)",
      color: "#0d1117",
      borderRadius: 3,
      border: "1px solid rgba(255,193,7,0.28)",
      boxShadow: "0 14px 36px rgba(0,0,0,0.22)",
      backdropFilter: "blur(12px)",
      transition: "transform 150ms ease, box-shadow 150ms ease, border-color 150ms ease",
      "&:hover": {
        borderColor: "rgba(255,193,7,0.52)",
        boxShadow: "0 18px 42px rgba(0,0,0,0.27)",
      },
    },
  };
  const btnPrimary = {
    variant: "contained",
    sx: {
      minHeight: 44,
      px: 2.2,
      background: "linear-gradient(135deg, #ffb300, #ffd54f)",
      color: "#111",
      boxShadow: "0 7px 18px rgba(255,179,0,0.28)",
      "&:hover": {
        background: "linear-gradient(135deg, #ffc107, #ffe082)",
        transform: "translateY(-1px)",
        boxShadow: "0 10px 24px rgba(255,179,0,0.38)",
      },
      "&.Mui-disabled": {
        background: "rgba(120,120,120,0.22)",
        color: "rgba(0,0,0,0.48)",
      },
      textTransform: "none",
      borderRadius: 2.3,
      fontWeight: 900,
      transition: "all 140ms ease",
    },
  };
  const btnGhost = {
    variant: "outlined",
    sx: {
      minHeight: 42,
      color: "#111",
      bgcolor: "rgba(255,255,255,0.72)",
      textTransform: "none",
      borderRadius: 2.3,
      borderColor: "rgba(17,17,17,0.45)",
      fontWeight: 800,
      "&:hover": {
        borderColor: "#111",
        bgcolor: "rgba(255,255,255,0.95)",
        transform: "translateY(-1px)",
      },
    },
  };

  const poolShare = Math.round((poolPercent || DEFAULT_POOL_PERCENT) * 100);

  const unlockPct = useMemo(() => {
    if (!unlockProgress.goal || unlockProgress.goal <= 0) return 0;
    return Math.min(
      100,
      Math.floor((unlockProgress.current / unlockProgress.goal) * 100)
    );
  }, [unlockProgress]);

  const activePower = Math.max(0, Number(perfil?.ppActivo ?? perfil?.pp ?? 0));
  const originalPower = Math.max(activePower, Number(perfil?.ppOriginal ?? activePower));
  const consumedPower = Math.max(0, Number(perfil?.ppQuema ?? originalPower - activePower));
  const powerPct = originalPower > 0 ? Math.min(100, (activePower / originalPower) * 100) : 0;

  const hasEnergy = energia >= energyCost;
  const hasHueso = hueso >= entryFee;
  const hasPower = activePower >= minPP;
  const canBattle = !loading && hasEnergy && hasHueso && hasPower;

  const battleDisabledReason = loading
    ? tr("Loading your Arena status...", "Cargando el estado de tu Arena...")
    : !hasPower
      ? tr(
          `You need at least ${fmt(minPP)} PP. Burn NFTs to obtain more Special Power.`,
          `Necesitas al menos ${fmt(minPP)} PP. Quema NFT para obtener más Poder Especial.`
        )
      : !hasEnergy
        ? tr(
            `You need ${fmt(energyCost)} energy. Current energy: ${fmt(energia)}.`,
            `Necesitas ${fmt(energyCost)} de energía. Energía actual: ${fmt(energia)}.`
          )
        : !hasHueso
          ? tr(
              `You need ${fmt(entryFee)} HUESO. Current balance: ${fmt(hueso)}.`,
              `Necesitas ${fmt(entryFee)} HUESO. Saldo actual: ${fmt(hueso)}.`
            )
          : tr(
              "Enter matchmaking. If no player is available, you will fight a BOT with similar PP.",
              "Entra al emparejamiento. Si no hay otro jugador disponible, lucharás contra un BOT con PP similar."
            );

  return (
    <Box sx={bgStyle}>
      <Box
        sx={{
          maxWidth: 1080,
          mx: "auto",
          "& .MuiPaper-root": { color: "#000" },
        }}
      >
        <Paper
          sx={{
            mb: 2,
            p: { xs: 1.5, sm: 2 },
            borderRadius: 3,
            color: "#fff !important",
            overflow: "hidden",
            position: "relative",
            background:
              "linear-gradient(135deg, rgba(10,14,28,0.96), rgba(39,22,4,0.94))",
            border: "1px solid rgba(255,193,7,0.5)",
            boxShadow: "0 18px 44px rgba(0,0,0,0.34)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              background:
                "radial-gradient(circle at 82% 10%, rgba(255,193,7,0.24), transparent 34%)",
            }}
          />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1.2}
            sx={{ position: "relative" }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 950, color: "#ffd54f", lineHeight: 1.05 }}
              >
                🐕‍🦺 {tr("Dog Arena", "Arena Canina")}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.45, color: "rgba(255,255,255,0.78)" }}>
                {tr("Burn NFTs for Special Power, fight, climb the ranking and earn weekly rewards.", "Quema NFT para obtener Poder Especial, lucha, sube en la clasificación y gana recompensas semanales.")}
              </Typography>
            </Box>

            <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
              <Stack direction="row" spacing={0.45}>
                <Button
                  size="small"
                  variant={language === "en" ? "contained" : "outlined"}
                  onClick={() => changeLanguage("en")}
                  sx={{
                    minHeight: 32,
                    px: 1.1,
                    color: language === "en" ? "#111" : "#fff",
                    borderColor: "rgba(255,213,79,0.75)",
                    background:
                      language === "en"
                        ? "linear-gradient(135deg, #ffb300, #ffd54f)"
                        : "rgba(255,255,255,0.06)",
                    fontWeight: 900,
                    textTransform: "none",
                  }}
                >
                  English
                </Button>
                <Button
                  size="small"
                  variant={language === "es" ? "contained" : "outlined"}
                  onClick={() => changeLanguage("es")}
                  sx={{
                    minHeight: 32,
                    px: 1.1,
                    color: language === "es" ? "#111" : "#fff",
                    borderColor: "rgba(255,213,79,0.75)",
                    background:
                      language === "es"
                        ? "linear-gradient(135deg, #ffb300, #ffd54f)"
                        : "rgba(255,255,255,0.06)",
                    fontWeight: 900,
                    textTransform: "none",
                  }}
                >
                  Español
                </Button>
              </Stack>

              {refreshing && (
                <Chip
                  size="small"
                  icon={<CircularProgress size={14} />}
                  label={tr("Updating", "Actualizando")}
                  sx={{ bgcolor: "rgba(255,255,255,0.92)", fontWeight: 800 }}
                />
              )}
              {username && (
                <Chip
                  label={`${tr("Player", "Jugador")}: ${username}`}
                  color="warning"
                  sx={{ fontWeight: 850 }}
                />
              )}
              <HelpTooltip title={tr("Updates profile, balances, ranking and all Arena logs at the same time.", "Actualiza al mismo tiempo el perfil, los saldos, la clasificación y todos los registros de la Arena.")}>
                <Button
                  {...btnGhost}
                  size="small"
                  startIcon={<RefreshRoundedIcon />}
                  onClick={() => refreshAll({ showMessage: true })}
                  disabled={refreshing}
                  sx={{ ...btnGhost.sx, minHeight: 34, color: "#111" }}
                >
                  {tr("Refresh all", "Actualizar todo")}
                </Button>
              </HelpTooltip>
            </Stack>
          </Stack>

          <Grid container spacing={1} sx={{ mt: 1.2, position: "relative" }}>
            <Grid item xs={6} sm={3}>
              <StatTile
                icon="☄️"
                label={tr("SPECIAL POWER", "PODER ESPECIAL")}
                value={fmt(activePower)}
                help={tr(`Consumable PP available now. Minimum to enter: ${fmt(minPP)} PP.`, `PP consumible disponible ahora. Mínimo para entrar: ${fmt(minPP)} PP.`)}
                accent="#ffb300"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatTile
                icon="⚡"
                label={tr("ENERGY", "ENERGÍA")}
                value={fmt(energia)}
                help={tr(`Each battle consumes ${fmt(energyCost)} energy.`, `Cada batalla consume ${fmt(energyCost)} de energía.`)}
                accent="#29b6f6"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatTile
                icon="🦴"
                label="HUESO"
                value={fmt(hueso)}
                help={tr(`Each battle costs ${fmt(entryFee)} HUESO.`, `Cada batalla cuesta ${fmt(entryFee)} HUESO.`)}
                accent="#ab47bc"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatTile
                icon="🏆"
                label="MMR"
                value={fmt(perfil.mmr)}
                help={tr("Your Arena ranking score. Successful battles increase it and improve your leaderboard position.", "Tu puntuación de clasificación en la Arena. Las batallas exitosas la aumentan y mejoran tu posición en la tabla.")}
                accent="#66bb6a"
              />
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={2}>
          {/* Left column */}
          <Grid item xs={12} md={7}>
            <Paper {...cardProps}>
              <SectionHeader
                icon="👤"
                title={tr("Player Profile", "Perfil del jugador")}
                help={tr("This panel shows your active Special Power, ranking score and combat history.", "Este panel muestra tu Poder Especial activo, puntuación de clasificación e historial de combate.")}
              />

              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} sm={7}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: { xs: 82, sm: 96 },
                        height: { xs: 82, sm: 96 },
                        flexShrink: 0,
                        borderRadius: 2.5,
                        overflow: "hidden",
                        border: "2px solid rgba(255,179,0,0.7)",
                        boxShadow: "0 7px 20px rgba(255,179,0,0.22)",
                        bgcolor: "#111827",
                      }}
                    >
                      <img
                        src={perfil?.avatar?.img || AVATARS[0].img}
                        alt="avatar"
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        onError={(e) => {
                          e.currentTarget.src = AVATARS[0].img;
                        }}
                      />
                    </Box>

                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: "1.05rem", fontWeight: 950 }} noWrap>
                        {username || perfil?.nombre || tr("Player", "Jugador")}
                      </Typography>

                      <Stack direction="row" spacing={0.65} flexWrap="wrap" sx={{ mt: 0.65, rowGap: 0.65 }}>
                        <HelpTooltip title={tr("MMR is your ranking score. A higher MMR places you higher on the leaderboard.", "El MMR es tu puntuación de clasificación. Un MMR más alto te coloca más arriba en la tabla.")}>
                          <Chip size="small" label={`🏆 MMR ${fmt(perfil.mmr)}`} sx={{ fontWeight: 850 }} />
                        </HelpTooltip>
                        <HelpTooltip title={tr("Total battles won in Arena.", "Total de batallas ganadas en la Arena.")}>
                          <Chip size="small" color="success" label={`✅ ${fmt(perfil.wins)} ${tr("wins", "victorias")}`} sx={{ fontWeight: 800 }} />
                        </HelpTooltip>
                        <HelpTooltip title={tr("Total battles lost in Arena.", "Total de batallas perdidas en la Arena.")}>
                          <Chip size="small" color="error" label={`❌ ${fmt(perfil.losses)} ${tr("losses", "derrotas")}`} sx={{ fontWeight: 800 }} />
                        </HelpTooltip>
                        <HelpTooltip title={tr("Consecutive wins without a loss.", "Victorias consecutivas sin una derrota.")}>
                          <Chip size="small" color="warning" label={`🔥 ${tr("Streak", "Racha")} ${fmt(perfil.streak)}`} sx={{ fontWeight: 800 }} />
                        </HelpTooltip>
                      </Stack>

                      <Box sx={{ mt: 1.15 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                          <HelpTooltip title={tr("Special Power is the total consumable power obtained from all burned NFTs. Arena battles reduce the available amount.", "El Poder Especial es el poder consumible total obtenido de todos los NFT quemados. Las batallas de Arena reducen la cantidad disponible.")}>
                            <Typography variant="body2" sx={{ fontWeight: 900, cursor: "help" }}>
                              ☄️ {tr("Special Power", "Poder Especial")}
                            </Typography>
                          </HelpTooltip>
                          <Typography variant="body2" sx={{ fontWeight: 950 }}>
                            {fmt(activePower)} / {fmt(originalPower)}
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={powerPct}
                          sx={{
                            mt: 0.6,
                            height: 9,
                            borderRadius: 999,
                            bgcolor: "rgba(0,0,0,0.1)",
                            "& .MuiLinearProgress-bar": {
                              borderRadius: 999,
                              background: powerPct > 25
                                ? "linear-gradient(90deg, #ff8f00, #ffd54f)"
                                : "linear-gradient(90deg, #d32f2f, #ff7043)",
                            },
                          }}
                        />
                        <Typography variant="caption" sx={{ display: "block", mt: 0.4, opacity: 0.72 }}>
                          {tr("Consumed", "Consumido")}: {fmt(consumedPower)} PP · {tr("Minimum to battle", "Mínimo para luchar")}: {fmt(minPP)} PP
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={5}>
                  <Box
                    sx={{
                      p: 1.1,
                      borderRadius: 2.2,
                      bgcolor: "rgba(10,14,28,0.045)",
                      border: "1px solid rgba(0,0,0,0.08)",
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 900, opacity: 0.72 }}>
                      {tr("CHOOSE AVATAR", "ELIGE AVATAR")}
                    </Typography>
                    <Stack direction="row" spacing={0.8} sx={{ mt: 0.7 }}>
                      {AVATARS.map((av) => {
                        const selected = perfil?.avatar?.id === av.id || perfil?.avatar?.img === av.img;
                        return (
                          <HelpTooltip key={av.id} title={tr(`Use ${av.name} as your Arena avatar.`, `Usa ${av.name} como tu avatar de Arena.`)}>
                            <Card
                              sx={{
                                width: { xs: 58, sm: 64 },
                                borderRadius: 2,
                                overflow: "hidden",
                                border: selected
                                  ? "2px solid #ffb300"
                                  : "1px solid rgba(0,0,0,0.14)",
                                boxShadow: selected ? "0 0 16px rgba(255,179,0,0.4)" : "none",
                              }}
                            >
                              <CardActionArea onClick={() => setAvatar(av.id)}>
                                <CardMedia component="img" image={av.img} alt={av.name} />
                              </CardActionArea>
                            </Card>
                          </HelpTooltip>
                        );
                      })}
                    </Stack>

                    <Divider sx={{ my: 1 }} />
                    <Stack direction="row" justifyContent="space-between">
                      <HelpTooltip title={tr("Battles played by you during the current reward cycle.", "Batallas jugadas por ti durante el ciclo actual de recompensas.")}>
                        <Typography variant="caption" sx={{ cursor: "help" }}>
                          ⚔️ {tr("Weekly battles", "Batallas semanales")}:  <b>{fmt(perfil.battlesThisWeek || 0)}</b>
                        </Typography>
                      </HelpTooltip>
                      <HelpTooltip title={tr("Activity tickets already earned during the current cycle.", "Tickets de actividad obtenidos durante el ciclo actual.")}>
                        <Typography variant="caption" sx={{ cursor: "help" }}>
                          🎟️ {tr("Activity", "Actividad")}:  <b>{fmt(perfil.activityTicketsThisWeek || 0)}</b>
                        </Typography>
                      </HelpTooltip>
                    </Stack>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            <Paper {...cardProps}>
              <SectionHeader
                icon="💰"
                title={tr("Resources & Weekly Economy", "Recursos y economía semanal")}
                help={tr("Everything required to fight and the current weekly reward economy. Hover or tap each card for help.", "Todo lo necesario para luchar y la economía semanal de recompensas. Pasa el cursor o toca cada tarjeta para ver ayuda.")}
              />

              <Grid container spacing={1}>
                <Grid item xs={6} sm={3}>
                  <StatTile
                    icon="⚡"
                    label={tr("ENERGY", "ENERGÍA")}
                    value={fmt(energia)}
                    help={tr(`Available energy. One battle consumes ${fmt(energyCost)}.`, `Energía disponible. Una batalla consume ${fmt(energyCost)}.`)}
                    accent="#29b6f6"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatTile
                    icon="🦴"
                    label="HUESO"
                    value={fmt(hueso)}
                    help={tr(`Your Arena HUESO balance. One battle costs ${fmt(entryFee)} HUESO.`, `Tu saldo HUESO de la Arena. Una batalla cuesta ${fmt(entryFee)} HUESO.`)}
                    accent="#ab47bc"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatTile
                    icon="💸"
                    label={tr("ENTRY FEE", "COSTO DE ENTRADA")}
                    value={fmt(entryFee)}
                    help={tr("HUESO charged when you enter matchmaking. Part goes to the weekly pool and part is burned.", "HUESO cobrado al entrar al emparejamiento. Una parte va al fondo semanal y otra parte se quema.")}
                    accent="#ef5350"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatTile
                    icon="🎯"
                    label={tr("ENERGY COST", "COSTO DE ENERGÍA")}
                    value={fmt(energyCost)}
                    help={tr("Energy deducted when a battle entry is accepted.", "Energía descontada cuando se acepta la entrada a una batalla.")}
                    accent="#26a69a"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatTile
                    icon="🏦"
                    label={tr("WEEKLY POOL", "FONDO SEMANAL")}
                    value={fmt(pool)}
                    suffix=" HUESO"
                    help={tr("Reward pool distributed to eligible ranked players after the global battle goal is reached.", "Fondo de recompensas distribuido entre los jugadores elegibles de la clasificación después de alcanzar la meta global de batallas.")}
                    accent="#ffa726"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatTile
                    icon="📊"
                    label={tr("FEE SPLIT", "DISTRIBUCIÓN DEL COSTO")}
                    value={`${poolShare}% / ${100 - poolShare}%`}
                    help={tr("First percentage goes to the weekly pool; the second percentage is permanently burned.", "El primer porcentaje va al fondo semanal; el segundo se quema permanentemente.")}
                    accent="#7e57c2"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatTile
                    icon="🔥"
                    label={tr("BURN THIS WEEK", "QUEMADO ESTA SEMANA")}
                    value={fmt(burnedWeek)}
                    help={tr("HUESO burned from Arena entries during the current reward cycle.", "HUESO quemado por las entradas a la Arena durante el ciclo actual de recompensas.")}
                    accent="#ff7043"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatTile
                    icon="♾️"
                    label={tr("TOTAL BURN", "QUEMA TOTAL")}
                    value={fmt(burnedTotal)}
                    help={tr("Historical HUESO burned through Arena entry fees.", "Total histórico de HUESO quemado mediante los costos de entrada de la Arena.")}
                    accent="#78909c"
                  />
                </Grid>
              </Grid>

              <Box
                sx={{
                  mt: 1.5,
                  p: 1.4,
                  borderRadius: 2.3,
                  border: unlockProgress.unlocked
                    ? "1px solid rgba(46,125,50,0.45)"
                    : "1px solid rgba(239,108,0,0.38)",
                  background: unlockProgress.unlocked
                    ? "linear-gradient(135deg, rgba(76,175,80,0.12), rgba(255,255,255,0.86))"
                    : "linear-gradient(135deg, rgba(255,152,0,0.12), rgba(255,255,255,0.86))",
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <HelpTooltip title={tr("All players contribute battles to this global goal. Weekly payouts only execute after the target is reached.", "Todos los jugadores aportan batallas a esta meta global. Los pagos semanales solo se ejecutan después de alcanzar el objetivo.")}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900, cursor: "help" }}>
                      🌍 {tr("Global weekly unlock", "Desbloqueo semanal global")}
                    </Typography>
                  </HelpTooltip>
                  <Chip
                    size="small"
                    color={unlockProgress.unlocked ? "success" : "warning"}
                    label={unlockProgress.unlocked ? tr("READY FOR PAYOUT", "LISTO PARA PAGAR") : `${unlockPct}% ${tr("COMPLETE", "COMPLETADO")}`}
                    sx={{ fontWeight: 900 }}
                  />
                </Stack>

                <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.8 }}>
                  <Typography variant="body2">
                    {tr("Battles", "Batallas")}: <b>{fmt(unlockProgress.current)} / {fmt(unlockProgress.goal || 0)}</b>
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {unlockProgress.unlocked ? tr("Unlocked", "Desbloqueado") : `${fmt(Math.max(0, (unlockProgress.goal || 0) - unlockProgress.current))} ${tr("remaining", "restantes")}`}
                  </Typography>
                </Stack>

                <LinearProgress
                  variant="determinate"
                  value={unlockPct}
                  sx={{
                    mt: 0.8,
                    height: 10,
                    borderRadius: 999,
                    bgcolor: "rgba(0,0,0,0.09)",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 999,
                      background: unlockProgress.unlocked
                        ? "linear-gradient(90deg, #2e7d32, #81c784)"
                        : "linear-gradient(90deg, #ef6c00, #ffca28)",
                    },
                  }}
                />
              </Box>
            </Paper>

            {/* Bone power table + Burn link */}
            <Paper {...cardProps}>
              <SectionHeader
                icon="🔥"
                title={tr("Power from Burned NFTs", "Poder de NFT quemados")}
                help={tr("This table shows how much Special Power each burned NFT adds. General and Special NFT values are both added to the same consumable PP total.", "Esta tabla muestra cuánto Poder Especial agrega cada NFT quemado. Los valores de NFT generales y especiales se suman al mismo total de PP consumible.")}
              />
              <Alert severity="info" sx={{ mb: 1.4, borderRadius: 2 }}>
                {tr("Burned NFTs permanently increase your original Special Power. Arena battles consume only the available PP.", "Los NFT quemados aumentan permanentemente tu Poder Especial original. Las batallas de Arena solo consumen el PP disponible.")}
              </Alert>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 0.5 }}
                  >
                    {tr("General", "General")}
                  </Typography>
                  <Box
                    sx={{
                      border: "1px solid #ddd",
                      borderRadius: 1,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        p: 1,
                        bgcolor: "#f7f7f7",
                        fontWeight: 700,
                      }}
                    >
                      <span>{tr("Rarity", "Rareza")}</span>
                      <span>HUESO ({tr("Power", "Poder")})</span>
                    </Box>
                    {RAREZAS.map((r) => (
                      <Box
                        key={`g-${r}`}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          p: 1,
                          borderTop: "1px solid #eee",
                        }}
                      >
                        <span>{r}</span>
                        <span>{fmt(BONE_VALUES.general[r])}</span>
                      </Box>
                    ))}
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 0.5 }}
                  >
                    {tr("Special", "Especial")}
                  </Typography>
                  <Box
                    sx={{
                      border: "1px solid #ddd",
                      borderRadius: 1,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        p: 1,
                        bgcolor: "#f7f7f7",
                        fontWeight: 700,
                      }}
                    >
                      <span>{tr("Rarity", "Rareza")}</span>
                      <span>HUESO ({tr("Power", "Poder")})</span>
                    </Box>
                    {RAREZAS.map((r) => (
                      <Box
                        key={`e-${r}`}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          p: 1,
                          borderTop: "1px solid #eee",
                        }}
                      >
                        <span>{r}</span>
                        <span>{fmt(BONE_VALUES.especial[r])}</span>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </Grid>

              {/* Estimated power from counts (if provided) */}
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 0.5 }}
                >
                  {tr("Estimated Accumulated Power", "Poder acumulado estimado")}
                </Typography>
                {estimatedPower ? (
                  <Stack
                    direction="row"
                    spacing={3}
                    flexWrap="wrap"
                  >
                    <Typography>
                      {tr("General", "General")}: {" "}
                      <b>{fmt(estimatedPower.general)}</b>
                    </Typography>
                    <Typography>
                      {tr("Special", "Especial")}: {" "}
                      <b>{fmt(estimatedPower.especial)}</b>
                    </Typography>
                    <Typography>
                      {tr("Total", "Total")}: {" "}
                      <b>{fmt(estimatedPower.total)}</b>
                    </Typography>
                  </Stack>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ opacity: 0.7 }}
                  >
                    {tr("N/A — will be calculated automatically when the backend exposes rarity totals.", "N/D — se calculará automáticamente cuando el backend exponga los totales por rareza.")}
                  </Typography>
                )}
              </Box>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 2 }} alignItems={{ sm: "center" }}>
                <HelpTooltip title={tr("Open the NFT burn page. Burning is permanent and adds Special Power according to NFT type and rarity.", "Abre la página de quema de NFT. La quema es permanente y agrega Poder Especial según el tipo y la rareza del NFT.")}>
                  <Button
                    component={RouterLink}
                    to={BURN_PATH}
                    {...btnPrimary}
                    startIcon={<LocalFireDepartmentRoundedIcon />}
                  >
                    {tr("Open Burn System", "Abrir sistema de quema")}
                  </Button>
                </HelpTooltip>

                <Chip
                  label={`${tr("Available PP", "PP disponible")}: ${fmt(activePower)} / ${fmt(originalPower)}`}
                  color={hasPower ? "success" : "error"}
                  sx={{ fontWeight: 900 }}
                />
              </Stack>
            </Paper>

            <Paper
              {...cardProps}
              sx={{
                ...cardProps.sx,
                background: canBattle
                  ? "linear-gradient(135deg, rgba(255,193,7,0.18), rgba(255,255,255,0.96))"
                  : "linear-gradient(135deg, rgba(239,83,80,0.12), rgba(255,255,255,0.96))",
                borderColor: canBattle ? "rgba(255,179,0,0.58)" : "rgba(211,47,47,0.35)",
              }}
            >
              <SectionHeader
                icon="⚔️"
                title={tr("Battle Center", "Centro de batalla")}
                help={tr("Entering a battle immediately charges the energy and HUESO costs. If no player is available, the system creates a BOT with similar power.", "Entrar a una batalla cobra inmediatamente los costos de energía y HUESO. Si no hay otro jugador disponible, el sistema crea un BOT con poder similar.")}
              />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="stretch">
                <HelpTooltip title={battleDisabledReason} placement="top">
                  <Box component="span" sx={{ display: "block", width: { xs: "100%", sm: "auto" } }}>
                    <Button
                      {...btnPrimary}
                      size="large"
                      startIcon={loading ? <CircularProgress size={18} /> : <SportsMmaRoundedIcon />}
                      onClick={joinBattle}
                      disabled={!canBattle}
                      fullWidth
                      sx={{ ...btnPrimary.sx, minWidth: { sm: 190 }, minHeight: 54 }}
                    >
                      {loading ? tr("Loading Arena...", "Cargando Arena...") : tr("Enter Battle", "Entrar a batalla")}
                    </Button>
                  </Box>
                </HelpTooltip>

                <Box sx={{ flex: 1 }}>
                  <Stack direction="row" spacing={0.7} flexWrap="wrap" sx={{ rowGap: 0.7 }}>
                    <Chip size="small" color={hasPower ? "success" : "error"} label={`☄️ PP ${fmt(activePower)} / ${fmt(minPP)} ${tr("min", "mín")}`} sx={{ fontWeight: 800 }} />
                    <Chip size="small" color={hasEnergy ? "success" : "error"} label={`⚡ ${fmt(energia)} / ${fmt(energyCost)}`} sx={{ fontWeight: 800 }} />
                    <Chip size="small" color={hasHueso ? "success" : "error"} label={`🦴 ${fmt(hueso)} / ${fmt(entryFee)}`} sx={{ fontWeight: 800 }} />
                  </Stack>
                  <Typography variant="body2" sx={{ mt: 0.8, opacity: 0.78 }}>
                    {canBattle
                      ? tr("Ready. PvP wins give better ticket rewards; a similar-power BOT is used as fallback.", "Listo. Las victorias PvP ofrecen mejores recompensas de tickets; se usa un BOT de poder similar como alternativa.")
                      : battleDisabledReason}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          {/* Right column */}
          <Grid item xs={12} md={5}>
            <Paper {...cardProps}>
              <SectionHeader
                icon="📡"
                title={tr("Live Arena Status", "Estado de la Arena en vivo")}
                help={tr("Shows the current season and how many players are waiting in matchmaking. Data refreshes automatically while this page is visible.", "Muestra la temporada actual y cuántos jugadores esperan en el emparejamiento. Los datos se actualizan automáticamente mientras esta página está visible.")}
              />

              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <StatTile
                    icon="🔎"
                    label={tr("IN QUEUE", "EN COLA")}
                    value={fmt(queueSize)}
                    help={tr("Players currently waiting for a PvP opponent. If no opponent is found, the BOT fallback is used.", "Jugadores esperando actualmente un oponente PvP. Si no se encuentra uno, se utiliza el BOT alternativo.")}
                    accent="#42a5f5"
                  />
                </Grid>
                <Grid item xs={6}>
                  <StatTile
                    icon="📅"
                    label={tr("SEASON", "TEMPORADA")}
                    value={season || "-"}
                    help={tr("Current Arena reward cycle used by the leaderboard and weekly payouts.", "Ciclo actual de recompensas de la Arena utilizado por la clasificación y los pagos semanales.")}
                    accent="#7e57c2"
                  />
                </Grid>
              </Grid>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1.3 }}>
                <HelpTooltip title={tr("Refreshes profile, balances, queue, ranking and all Arena logs in parallel.", "Actualiza en paralelo el perfil, los saldos, la cola, la clasificación y todos los registros de la Arena.")}>
                  <Button
                    {...btnPrimary}
                    fullWidth
                    startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshRoundedIcon />}
                    onClick={() => refreshAll({ showMessage: true })}
                    disabled={refreshing}
                  >
                    {tr("Refresh Arena", "Actualizar Arena")}
                  </Button>
                </HelpTooltip>

                <HelpTooltip title={tr("Reloads the leaderboard and reward previews without entering a battle.", "Recarga la clasificación y las vistas previas de recompensas sin entrar a una batalla.")}>
                  <Button
                    {...btnGhost}
                    fullWidth
                    startIcon={<EmojiEventsRoundedIcon />}
                    onClick={async () => {
                      setRefreshing(true);
                      await Promise.allSettled([loadLeaderboard(), loadFeed(), loadBattleFeed(), loadResetFeed()]);
                      setRefreshing(false);
                      notify(tr("Ranking updated", "Clasificación actualizada"), "success");
                    }}
                    disabled={refreshing}
                  >
                    {tr("Update Ranking", "Actualizar clasificación")}
                  </Button>
                </HelpTooltip>
              </Stack>
            </Paper>

            {/* {tr("Arena Activity", "Actividad de la Arena")} Feed */}
            <Paper
              {...cardProps}
              sx={{
                ...cardProps.sx,
                maxHeight: 260,
                overflowY: "auto",
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: "#000", fontWeight: 700 }}
                >
                  {tr("Arena Activity", "Actividad de la Arena")}
                </Typography>

                <HelpTooltip title={tr("Reloads rewards, battle economy and payout activity logs.", "Recarga las recompensas, la economía de batalla y los registros de actividad de pagos.")}>
                  <Button
                    {...btnPrimary}
                    size="small"
                    startIcon={<RefreshRoundedIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      ...btnPrimary.sx,
                      minHeight: 32,
                      px: 1.2,
                      py: 0.35,
                      fontSize: "0.68rem",
                      lineHeight: 1.2,
                    }}
                    onClick={() => Promise.allSettled([loadFeed(), loadBattleFeed(), loadResetFeed()])}
                  >
                    {tr("Refresh", "Actualizar")}
                  </Button>
                </HelpTooltip>
              </Stack>

              {arenaActivityFeed.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.6, color: "#000" }}
                >
                  {tr("No recent rewards yet.", "Todavía no hay recompensas recientes.")}
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {arenaActivityFeed.map((log, idx) => {
                    const dateStr = new Date(log.ts).toLocaleTimeString(
                      [],
                      { hour: "2-digit", minute: "2-digit" }
                    );
                  


                    let label = "";
                    if (log.razon === "WIN_DROP") {
                      label = tr("Win reward", "Recompensa por victoria");
                    } else if (log.razon === "ACTIVITY") {
                      label = tr("Activity bonus", "Bono de actividad");
                    } else if (log.razon === "POOL_PAYOUT") {
                      label = tr("Weekly payout", "Pago semanal");
                    } else if (log.razon === "VIP_PAYOUT") {
                      label = tr("VIP weekly payout", "Pago VIP semanal");
                    } else if (/_PAYOUT$/.test(String(log.razon || "")) && log.symbol) {
                      // NUEVO: cualquier payout de token (ej: SWAP_PAYOUT)
                      label = `${log.symbol} ${tr("payout", "pagado")}`;
                    } else {
                      label = log.razon || tr("Reward", "Recompensa");
                    }

                    const isVipPayout = log.razon === "VIP_PAYOUT";

                    return (
                      <Box
                        key={idx}
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          border: "1px solid rgba(0,0,0,0.1)",
                          backgroundColor:
                            "rgba(255,255,255,0.6)",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: "#000",
                            display: "block",
                            mb: 0.5,
                          }}
                        >
                          {dateStr}{" "}
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: "#000",
                              ml: 1,
                            }}
                          >
                            {log.user}
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{
                                ml: 0.5,
                                fontWeight: 400,
                                opacity: 0.6,
                                color: "#000",
                              }}
                            >
                              ({label})
                            </Typography>
                          </Typography>
                        </Typography>

                        {/* Render TOKEN vs VIP vs Tickets */}
                        {(log.symbol && /_PAYOUT$/.test(String(log.razon || ""))) ? (
                          // NUEVO: pagos de tokens (ej: SWAP.HIVE)
                          <Typography
                            variant="body2"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                              color: "#000",
                              fontWeight: 700,
                              ml: 4.5,
                            }}
                          >
                            💱 +{fmtToken(log.qty, swapDecimals)} {log.symbol}

                          </Typography>
                        ) : isVipPayout ? (
                          <Typography
                            variant="body2"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                              color: "#000",
                              fontWeight: 700,
                              ml: 4.5,
                            }}
                          >
                            🍖 +{fmtVip(log.qty)} {vipSymbol}
                          </Typography>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                              color: "#000",
                              fontWeight: 600,
                              ml: 4.5,
                            }}
                          >
                            <img
                              src={TICKET_GIF}
                              alt="ticket"
                              style={{
                                width: 20,
                                height: 20,
                                objectFit: "contain",
                                marginRight: 4,
                              }}
                            />
                            +{fmt(log.qty)} {tr(Number(log.qty) === 1 ? "ticket" : "tickets", Number(log.qty) === 1 ? "ticket" : "tickets")}
                          </Typography>
                        )}

                        {log.detalle && (
                          <Box sx={{ ml: 4.5, mt: 0.25 }}>
                            <Chip
                              size="small"
                              color="warning"
                              label={log.detalle}
                              sx={{ height: 22, fontWeight: 800 }}
                            />
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Paper>

            {/* Battle / Economy Feed */}
            <Paper
              {...cardProps}
              sx={{
                ...cardProps.sx,
                maxHeight: 260,
                overflowY: "auto",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "#000",
                  fontWeight: 700,
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  rowGap: 1,
                }}
              >
                <span>{tr("Battle Log / Economy", "Registro de batallas / Economía")}</span>
                <Chip
                  size="small"
                  label={`${tr("Burn Wk", "Quemado sem.")}: ${fmt(
                    burnedWeek
                  )} | Pool: ${fmt(pool)}`}
                  sx={{ fontWeight: 700 }}
                  color="warning"
                />
              </Typography>

              {battleFeed.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.6, color: "#000" }}
                >
                  {tr("No recent battles logged.", "No hay batallas recientes registradas.")}
                </Typography>
              ) : (
                <Stack spacing={1}>
{battleFeed.map((ev, idx) => {
  const dateStr = new Date(ev.ts).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  const vsLine = ev.players
    ? ev.players
        .map((p) => `${p.nombre} ${p.won ? "✅" : "❌"}`)
        .join("  vs  ")
    : "";

  return (
    <Box
      key={idx}
      sx={{
        p: 1,
        borderRadius: 1,
        border: "1px solid rgba(0,0,0,0.1)",
        backgroundColor: "rgba(255,255,255,0.6)",
      }}
    >
      {/* cabecera */}
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: "#000",
          display: "block",
          mb: 0.5,
        }}
      >
        {dateStr}{" "}
        <Typography
          component="span"
          variant="body2"
          sx={{ fontWeight: 700, color: "#000", ml: 1 }}
        >
          {ev.type === "bot" ? tr("BOT Match", "Partida BOT") : tr("PvP Match", "Partida PvP")}{" "}
          <Typography
            component="span"
            variant="body2"
            sx={{
              ml: 0.5,
              fontWeight: 400,
              opacity: 0.6,
              color: "#000",
            }}
          >
            ({tr("Winner", "Ganador")}: {ev.winner})
          </Typography>
        </Typography>
      </Typography>

      {/* chips por jugador: verde si ganó, rojo si perdió */}
      {Array.isArray(ev.players) && ev.players.length > 0 && (
        <Box
          sx={{
            ml: 4.5,
            mt: 0.5,
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
          }}
        >
          {ev.players.map((p, i) => (
            <Chip
              key={p.userId || p.nombre || i}
              size="small"
              label={`${p.nombre} ${p.won ? tr("🏆WON🏆", "🏆GANÓ🏆") : tr("😢LOST😢", "😢PERDIÓ😢")}`}
              color={p.won ? "success" : "error"} // verde si ganó, rojo si perdió
              sx={{ height: 22, fontWeight: 800 }}
            />
          ))}
        </Box>
      )}

      {/* línea con emojis (opcional, como ahora) */}
      <Typography
        variant="body2"
        sx={{
          color: "#000",
          fontWeight: 600,
          ml: 4.5,
          wordBreak: "break-word",
        }}
      >
        {vsLine}
      </Typography>

      <Box sx={{ ml: 4.5, mt: 0.5 }}>
        <Chip
          size="small"
          color="warning"
          label={`+${tr("Pool", "Fondo")}: ${fmt(ev.poolAdded || 0)} HUESO · ${tr("Burned", "Quemado")}: ${fmt(
            ev.burnAdded || 0
          )} HUESO`}
          sx={{ height: 22, fontWeight: 800 }}
        />
      </Box>
    </Box>
  );
})}




                </Stack>
              )}
            </Paper>

            {/* === NUEVO: Season Reset / Payout Log === */}
            <Paper
              {...cardProps}
              sx={{
                ...cardProps.sx,
                maxHeight: 260,
                overflowY: "auto",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "#000",
                  fontWeight: 700,
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  rowGap: 1,
                }}
              >
                  <span>{tr("Season Reset / Payout Log", "Registro de reinicio / Pagos de temporada")}</span>
                {/* Si el backend manda la clave de temporada en el primer item */}
                {mergedResetFeed?.[0]?.seasonKey && (
                  <Chip
                    size="small"
                    label={`${tr("Season", "Temporada")}: ${season || mergedResetFeed?.[0]?.seasonKey || "-"}`}
                    sx={{ fontWeight: 700 }}
                    color="default"
                  />
                )}

              </Typography>

                            {mergedResetFeed.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.6, color: "#000" }}
                >
                  {tr("No reset or payout events yet.", "Todavía no hay eventos de reinicio o pago.")}
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {mergedResetFeed.map((ev, idx) => {
                    const dateStr = new Date(ev.ts).toLocaleString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    });

                  


                    // Etiqueta amigable según tipo/razón
                  // Etiqueta amigable según tipo/razón/acción
const razon = ev.razon || ev.type || ev.action || "EVENT";
let label = "";

// 1) Casos de reset
if (ev.action === "FULL_RESET") {
  label = tr("Season reset + payouts", "Reinicio de temporada + pagos");
} else if (ev.action === "SOFT_RESET" && ev.unlocked === false) {
  // ⚠️ ESTE ES TU CASO: NO SE HIZO PAYOUT
  label = tr("NO PAYOUT (goal not reached)", "SIN PAGO (meta no alcanzada)");
} else if (
  ["SEASON_RESET", "RESET"].includes(razon) ||
  (ev.action === "SOFT_RESET" && ev.unlocked === true)
) {
  label = tr("Season reset", "Reinicio de temporada");
}
// 2) Payouts de pool / VIP / tickets
else if (["POOL_PAYOUT", "POOL", "POOL_AWARD"].includes(razon)) {
  label = tr("Pool payout", "Pago del fondo");
} else if (["VIP_PAYOUT", "VIP", "VIP_AWARD"].includes(razon)) {
  label = `${vipSymbol} ${tr("payout", "pagado")}`;
} else if (
  ["TICKETS_PAYOUT", "TICKETS_DISTRIBUTED"].includes(razon)
) {
  label = tr("Tickets distributed", "Tickets distribuidos");
} else if (razon === "RESET_SKIPPED") {
  label = tr("Reset skipped", "Reinicio omitido");
}
// 3) fallback
else {
  label = razon;
}


                    // Campos flexibles para cantidades (totales o por usuario)
                    const seasonKey = ev.seasonKey || ev.season || "-";
                    const huesoSent =
                      ev.huesoSent ?? ev.hueso ?? ev.pool ?? ev.amountHueso ?? 0;
                    const ticketsSent =
                      ev.ticketsSent ?? ev.tickets ?? ev.amountTickets ?? 0;
                    const vipSent =
                      ev.vipSent ?? ev.vip ?? ev.amountVip ?? 0;

                    // === NUEVO: tokens adicionales flexibles (por ejemplo SWAP.HIVE)
                    // Soporta: ev.tokens (array de {symbol, amount, decimals}),
                    //          ev.token (objeto único),
                    //          ev.swapTokens / ev.extraTokens (arrays alternativos).
                    const tokenItems = [
                      ...(Array.isArray(ev.tokens) ? ev.tokens : []),
                      ...(Array.isArray(ev.swapTokens) ? ev.swapTokens : []),
                      ...(Array.isArray(ev.extraTokens) ? ev.extraTokens : []),
                      ...(ev.token && typeof ev.token === "object" ? [ev.token] : []),
                    ].filter(Boolean);

                    const hasUser = !!ev.user; // línea por jugador
                    const note = ev.note || ev.detalle;

                    return (
                      <Box
                        key={idx}
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          border: "1px solid rgba(0,0,0,0.1)",
                          backgroundColor: "rgba(255,255,255,0.6)",
                        }}
                      >
                        {/* Cabecera */}
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: "#000",
                            display: "block",
                            mb: 0.25,
                          }}
                        >
                          {dateStr} • {label}
                          {seasonKey && (
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{ ml: 1, opacity: 0.75 }}
                            >
                              ({tr("Season", "Temporada")}: {seasonKey})
                            </Typography>
                          )}
                          {hasUser && (
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{ ml: 1, fontWeight: 800 }}
                            >
                              • {ev.user}
                            </Typography>
                          )}
                        </Typography>

                        {/* Montos */}
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#000",
                            fontWeight: hasUser ? 700 : 600,
                            ml: 0.5,
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "12px",
                          }}
                        >
                          🦴 HUESO: <b>{fmt(huesoSent)}</b>
                          <span style={{ display: "inline-flex", alignItems: "center" }}>
                            🎟️ {tr("Tickets", "Tickets")}: <b style={{ marginLeft: 4 }}>{fmt(ticketsSent)}</b>
                          </span>
                          <span>🍖 {vipSymbol}: <b>{fmtVip(vipSent)}</b></span>

                          {/* 3) NUEVO: lista de tokens adicionales (ej. SWAP.HIVE) */}
                          {tokenItems.map((tk, j) => {
                            const sym = tk?.symbol || SWAP_SYMBOL;
                            const amt =
                              typeof tk?.amount === "number"
                                ? tk.amount
                                : (typeof tk?.qty === "number" ? tk.qty : 0);
                            const dec = Number(
                              (typeof tk?.decimals === "number" ? tk.decimals : undefined) ??
                              SWAP_DECIMALS
                            );
                            return (
                              <span key={`${sym}-${j}`} style={{ display: "inline-flex", alignItems: "center" }}>
                                💱 {sym}: <b style={{ marginLeft: 4 }}>{fmtToken(amt, dec)}</b>
                              </span>
                            );
                          })}
                        </Typography>

                        {/* txid opcional (si viene del backend) */}
                        {ev.txid && (
                          <Typography
                            variant="caption"
                            sx={{ opacity: 0.75, color: "#000", ml: 0.5, display: "block", mt: 0.25 }}
                          >
                            tx:&nbsp;
                            <a
                              href={`https://he.dtools.dev/tx/${ev.txid}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {String(ev.txid).slice(0, 12)}…
                            </a>
                          </Typography>
                        )}

                        {/* Nota / detalle */}
                        {note && (
                          <Box sx={{ ml: 0.5, mt: 0.25 }}>
                            <Chip
                              size="small"
                              color="warning"
                              label={note}
                              sx={{ height:22,fontWeight:800 }}
                            />
                          </Box>
                        )}

                        {/* Extra opcional */}
                        {ev.extra && (
                          <Typography
                            variant="caption"
                            sx={{
                              opacity: 0.6,
                              color: "#000",
                              ml: 0.5,
                              display: "block",
                              mt: 0.25,
                              fontFamily: "monospace",
                              wordBreak: "break-word",
                            }}
                          >
                            {JSON.stringify(ev.extra)}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Leaderboard */}
      <Box sx={{ maxWidth: 1080, mx: "auto", mt: 1 }}>
        <Paper
          {...cardProps}
          sx={{
            ...cardProps.sx,
            backgroundColor: "rgba(0,0,0,0.4)",
            color: "#fff",
            boxShadow: "0 8px 24px rgba(0,0,0,0.6)",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: 1,
              color: "#ffcc00",
              fontWeight: 800,
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            🏆 {tr("Leaderboard", "Clasificación")}
          </Typography>

          <Typography
            variant="body2"
            sx={{ opacity: 0.9, mb: 1, color: "#fff" }}
          >
            {tr("Season", "Temporada")}: <b>{season}</b> — {tr("Weekly Pool", "Fondo semanal")}:{" "}
            <b>{fmt(pool)}</b> HUESO
          </Typography>

          {/* barra de unlock también acá */}
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 1,
              backgroundColor: "rgba(0,0,0,0.5)",
              border: "1px solid rgba(255,255,255,0.3)",
              color: "#fff",
              maxWidth: 480,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1, flexWrap: "wrap", rowGap: 1 }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: "#fff" }}
              >
                {tr("Weekly Unlock", "Desbloqueo semanal")}
              </Typography>

              <Chip
                size="small"
                color={
                  unlockProgress.unlocked ? "success" : "error"
                }
                label={
                  unlockProgress.unlocked
                    ? tr("UNLOCKED", "DESBLOQUEADO")
                    : tr("LOCKED", "BLOQUEADO")
                }
                sx={{
                  fontWeight: 700,
                  color: "#fff",
                }}
              />
            </Stack>

            <Typography
              variant="body2"
              sx={{ mb: 1, color: "#fff" }}
            >
              {tr("Battles this week (global)", "Batallas esta semana (global)")}: {" "}
              <b>
                {fmt(unlockProgress.current)}/
                {fmt(unlockProgress.goal || 0)}
              </b>
            </Typography>

            <LinearProgress
              variant="determinate"
              value={unlockPct}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor:
                  "rgba(255,255,255,0.15)",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 4,
                  backgroundColor: unlockProgress.unlocked
                    ? "rgba(76,175,80,0.9)"
                    : "rgba(255,235,59,0.9)",
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.5,
                opacity: 0.8,
                color: "#fff",
              }}
            >
              {tr("Pool distributes only if unlocked when the week resets.", "El fondo solo se distribuye si está desbloqueado cuando se reinicia la semana.")}
            </Typography>
          </Box>

          <Box
            sx={{
              overflowX: "auto",
              borderRadius: 1,
              border:
                "1px solid rgba(255,255,255,0.3)",
              backgroundColor:
                "rgba(0,0,0,0.4)",
              color: "#fff",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "40px 1fr 70px 90px 90px 140px 140px 140px 140px",
                p: 1,
                bgcolor: "rgba(255,255,255,0.08)",
                color: "#fff",
                fontWeight: 700,
                borderBottom:
                  "1px solid rgba(255,255,255,0.2)",
              }}
            >
              <span>#</span>
              <span>🙍🏻‍♂️ {tr("Player", "Jugador")}</span>
              <span>🔝 MMR</span>
              <span>☄️ PP</span>
              <span>⚔️ {tr("Weekly Battles", "Batallas semanales")}</span>
              <span>🦴 HUESO ({tr("preview", "vista previa")})</span>
              <span>🎟️ Tickets ({tr("preview", "vista previa")})</span>
              <span>🍖 {vipSymbol} ({tr("preview", "vista previa")})</span>
              <span>💱 {swapSymbol} ({tr("preview", "vista previa")})</span>

            </Box>

            {/* Rows */}
            {leader.length === 0 && (
              <Box
                sx={{
                  p: 2,
                  opacity: 0.7,
                  color: "#fff",
                }}
              >
                {tr("No positions yet", "Todavía no hay posiciones")}
              </Box>
            )}

            {leader.map((r, i) => {
              const av = getAvatarForRow(r);
              return (
                <Box
                  key={r.userId || `${r.nombre}-${i}`}
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "40px 1fr 70px 90px 90px 140px 140px 140px 140px",
                    alignItems: "center",
                    p: 1,
                    borderTop:
                      "1px solid rgba(255,255,255,0.12)",
                    color: "#fff",
                    bgcolor:
                      i % 2 === 0
                        ? "rgba(255,255,255,0.07)"
                        : "rgba(255,255,255,0.03)",
                  }}
                >
                  {/* rank */}
                  <Box
                    component="span"
                    sx={{ fontWeight: 600 }}
                  >
                    {i + 1}
                  </Box>

                  {/* player avatar + name */}
                  <Box
                    component="span"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "6px",
                        overflow: "hidden",
                        border:
                          "1px solid rgba(147, 145, 145, 0.21)",
                        flexShrink: 0,
                        backgroundColor: "#000",
                      }}
                    >
                      <img
                        src={av.img}
                        alt={av.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                        onError={(e) => {
                          e.currentTarget.src =
                            AVATARS[0].img;
                        }}
                      />
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{
                        color: "#fff",
                        fontWeight: 500,
                        textOverflow:
                          "ellipsis",
                        overflow: "hidden",
                        whiteSpace:
                          "nowrap",
                      }}
                    >
                      {r.nombre}
                    </Typography>
                  </Box>

                  {/* mmr */}
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 500,
                      textAlign: "left",
                    }}
                  >
                    {fmt(r?.mmr || 0)}
                  </Box>

                  {/* PP */}
                  <Box 
                    component="span" 
                    sx={{ 
                      fontWeight: 500, 
                      textAlign: "left",
                    }}
                  >
                    {fmt(getRowPP(r))}
                  </Box>

                  {/* weekly battles */}
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 500,
                      textAlign: "left",
                    }}
                  >
                    {fmt(r?.battlesThisWeek || 0)}
                  </Box>

                  {/* preview hueso */}
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 500,
                      textAlign: "left",
                    }}
                  >
                    {fmt(r?.preview?.hueso || 0)}
                  </Box>

                  {/* preview tickets */}
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 500,
                      textAlign: "left",
                    }}
                  >
                    {fmt(r?.preview?.tickets || 0)}
                  </Box>

                  {/* VIP semanal (preview/calculado por backend) */}
                  <Box
                    component="span" 
                    sx={{ fontWeight: 600, textAlign: "left" }}
                  >
                    {fmtVip(r?.vip ?? r?.preview?.vip)}
                  </Box>

                  {/* SWAP preview */}
                  <Box
                    component="span"
                    sx={{ fontWeight: 600, textAlign: "left" }}
                  >
                    {getRowSwapPreview(r, swapDecimals, swapSymbol)}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Paper>
      </Box>

      {/* Battle Modal */}
      <Dialog
        open={battleOpen}
        onClose={() => {
          if (battlePhase === "result") {
            setBattleOpen(false);
            setAnimDone(false);
            setResultReady(false);
            resultBufferRef.current = null;
            if (animTimerRef.current)
              clearTimeout(animTimerRef.current);
            loadStatus();
            loadFeed();
            loadBattleFeed();
            loadResetFeed();
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            color: "#fffefeff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {battlePhase === "anim"
            ? tr("Battle in Progress...", "Batalla en progreso...")
            : battleResult?.title || tr("Result", "Resultado")}
          <Box>
            <HelpTooltip title={muted ? tr("Turn battle sound on.", "Activar el sonido de la batalla.") : tr("Mute the battle video.", "Silenciar el video de la batalla.")} placement="bottom">
              <IconButton
                onClick={() => {
                  setMuted((m) => !m);
                  const v = videoRef.current;
                  if (v) v.muted = !v.muted;
                }}
                size="small"
                sx={{ color: "#fff" }}
              >
                {muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
              </IconButton>
            </HelpTooltip>
            <HelpTooltip title={tr("Replay or resume the battle animation.", "Reproducir nuevamente o continuar la animación de batalla.")} placement="bottom">
              <IconButton
                onClick={() => {
                  const v = videoRef.current;
                  if (v) v.play().catch(() => {});
                }}
                size="small"
                sx={{ color: "#fff" }}
              >
                <PlayArrowIcon />
              </IconButton>
            </HelpTooltip>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ color: "#000" }}>
          {battlePhase === "anim" ? (
            <Box>
              <Box
                sx={{
                  width: "100%",
                  height: 260,
                  mb: 2,
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid #ddd",
                  background: "#c9c6c6ff",
                }}
              >
                <video
                  ref={videoRef}
                  src={BATTLE_ANIM}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                  autoPlay
                  controls
                  playsInline
                />
              </Box>
              <LinearProgress />
              <Typography
                sx={{
                  mt: 1,
                  textAlign: "center",
                  fontWeight: 600,
                }}
              >
                {animStatusText === "FIGHTING"
                  ? tr("Opponent found — fighting...", "Oponente encontrado — peleando...")
                  : tr("Searching opponent...", "Buscando oponente...")} {battleCountdown}s
              </Typography>
              <Typography
                sx={{
                  textAlign: "center",
                  fontSize: "0.8rem",
                  opacity: 0.7,
                }}
              >
                {tr("(Tickets x2 if PvP win)", "(Tickets x2 si ganas en PvP)")}
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* VS CARD */}
              {battleResult?.detail?.matchup && (
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: 2,
                    border: "1px solid #ffffffff",
                    backgroundColor: "rgba(240, 235, 235, 0.03)",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 800 }}
                  >
                    {tr("Matchup", "Enfrentamiento")}
                  </Typography>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1.5}
                  >
                    {/* Me */}
                    <Stack
                      spacing={0.5}
                      alignItems="center"
                      sx={{ minWidth: 120, flex: 1 }}
                    >
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: "10px",
                          overflow: "hidden",
                          border: "1px solid #ddd",
                          background: "#c7c7c7ff",
                        }}
                      >
                        <img
                          src={battleResult.detail.matchup.me.avatarUrl || AVATARS[0].img}
                          alt="me"
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          onError={(e) => { e.currentTarget.src = AVATARS[0].img; }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {battleResult.detail.matchup.me.name || tr("You", "Tú")}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        MMR: {fmt(battleResult.detail.matchup.me.mmr || 0)}
                      </Typography>
                    </Stack>

                    {/* VS */}
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 900, mx: 1 }}
                    >
                      VS
                    </Typography>

                    {/* Opp */}
                    <Stack
                      spacing={0.5}
                      alignItems="center"
                      sx={{ minWidth: 120, flex: 1 }}
                    >
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: "10px",
                          overflow: "hidden",
                          border: "1px solid #ddd",
                          background: "#c3c2c2ff",
                        }}
                      >
                        <img
                          src={battleResult.detail.matchup.opp.avatarUrl || AVATARS[0].img}
                          alt="opp"
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          onError={(e) => { e.currentTarget.src = AVATARS[0].img; }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {battleResult.detail.matchup.opp.name || tr("Opponent", "Oponente")}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        MMR: {fmt(battleResult.detail.matchup.opp.mmr || 0)}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Typography
                    variant="caption"
                    sx={{ display: "block", mt: 1, opacity: 0.8, textAlign: "center" }}
                  >
                    {tr("Winner", "Ganador")}: <b>{battleResult?.detail?.winnerName || (battleResult?.detail?.won ? tr("You", "Tú") : tr("Opponent", "Oponente"))}</b>
                  </Typography>
                </Box>
              )}

              <Typography
                variant="body1"
                sx={{ mb: 1 }}
              >
                {battleResult?.detail?.won
                  ? tr("✅ You won!", "✅ ¡Ganaste!")
                  : tr("❌ You lost", "❌ Perdiste")}{" "}
                — {tr("Rounds", "Rondas")}: {" "}
                {battleResult?.detail?.rounds ?? 3}
              </Typography>
              <Divider sx={{ my: 1 }} />
              {/* Power change summary */}
              {battleResult?.detail?.pp && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 0.5 }}
                  >
                    {tr("Power Change", "Cambio de poder")}
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2">
                        {tr("Before", "Antes")}: {" "}
                        <b>
                          {fmt(
                            battleResult.detail.pp
                              .before
                          )}
                        </b>
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2">
                        {tr("Consumed", "Consumido")}: {" "}
                        <b>
                          {fmt(
                            battleResult.detail.pp
                              .consumed
                          )}
                        </b>
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2">
                        {tr("Refund", "Reembolso")}: {" "}
                        <b>
                          {fmt(
                            battleResult.detail.pp
                              .refund
                          )}
                        </b>
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2">
                        {tr("Final", "Final")}: {" "}
                        <b>
                          {fmt(
                            battleResult.detail.pp
                              .final
                          )}
                        </b>
                      </Typography>
                    </Grid>
                  </Grid>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.5,
                      opacity: 0.8,
                    }}
                  >
                    {tr("Net loss", "Pérdida neta")}: {" "}
                    <b>
                      {fmt(
                        battleResult.detail.pp
                          .lost
                      )}
                    </b>{" "}
                    PP
                  </Typography>
                </Box>
              )}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <HelpTooltip title={tr("Close the result and refresh your current PP, balances and Arena activity.", "Cierra el resultado y actualiza tu PP, saldos y actividad de la Arena.")}>
                  <Button
                    {...btnPrimary}
                    onClick={async () => {
                      setBattleOpen(false);
                      setAnimDone(false);
                      setResultReady(false);
                      resultBufferRef.current = null;
                      if (animTimerRef.current) clearTimeout(animTimerRef.current);
                      await refreshAll();
                    }}
                  >
                    {tr("Close & Update", "Cerrar y actualizar")}
                  </Button>
                </HelpTooltip>
                <HelpTooltip title={tr("Refresh the leaderboard and reward previews after this battle.", "Actualiza la clasificación y las vistas previas de recompensas después de esta batalla.")}>
                  <Button
                    {...btnGhost}
                    startIcon={<EmojiEventsRoundedIcon />}
                    onClick={async () => {
                      await Promise.allSettled([loadLeaderboard(), loadFeed(), loadBattleFeed(), loadResetFeed()]);
                      notify(tr("Ranking updated", "Clasificación actualizada"), "success");
                    }}
                  >
                    {tr("Update Ranking", "Actualizar clasificación")}
                  </Button>
                </HelpTooltip>
              </Stack>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Reward Modal */}
      <Dialog
        open={rewardOpen}
        onClose={() => {
          setRewardOpen(false);
          loadStatus();
          loadFeed();
          loadBattleFeed();
          loadResetFeed();
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: "#ffffffff" }}>
          🎉 {tr("Reward", "Recompensa")}
        </DialogTitle>
        <DialogContent sx={{ color: "#000" }}>
          {rewardInfo ? (
            <>
              {(Array.isArray(rewardInfo)
                ? rewardInfo
                : [rewardInfo]
              ).map((rew, idx) => (
                <Box
                  key={idx}
                  sx={{
                    mb: 2,
                    textAlign: "center",
                  }}
                >
                  {rew.type === "VIPHUESO" && (
                    <Typography
                      variant="h6"
                      sx={{ mb: 1 }}
                    >
                      {tr("You received", "Recibiste")}{" "}
                        <b>
                        {fmtVip(rew.amount)}
                        </b>
                         {vipSymbol}!
                    </Typography>
                  )}

                  {rew.type === "TICKET" && (
                    <>
                      <img
                        src={TICKET_GIF}
                        alt={tr("Ticket Reward", "Recompensa de ticket")}
                        style={{
                          width: 120,
                          height: 120,
                          objectFit: "contain",
                          display:
                            "block",
                          margin:
                            "0 auto 8px",
                        }}
                      />
                      <Typography
                        variant="h6"
                        align="center"
                      >
                        {tr("You received", "Recibiste")}{" "}
                        <b>
                          {fmt(
                            rew.amount
                          )}
                        </b>{" "}
                        {tr("Ticket", "Ticket")}
                        {Number(
                          rew.amount
                        ) === 1
                          ? ""
                          : "s"}{" "}
                        ({tr("Win reward", "Recompensa por victoria")})!
                      </Typography>
                    </>
                  )}

                  {rew.type === "ACTIVITY" && (
                    <>
                      <img
                        src={TICKET_GIF}
                        alt={tr("Activity Ticket", "Ticket de actividad")}
                        style={{
                          width: 120,
                          height: 120,
                          objectFit: "contain",
                          display:
                            "block",
                          margin:
                            "0 auto 8px",
                        }}
                      />
                      <Typography
                        variant="h6"
                        align="center"
                      >
                        {tr("Activity bonus!", "¡Bono de actividad!")}{" "}
                        <b>
                          {fmt(
                            rew.amount
                          )}
                        </b>{" "}
                        {tr("Ticket", "Ticket")}
                        {Number(
                          rew.amount
                        ) === 1
                          ? ""
                          : "s"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          opacity: 0.8,
                          textAlign:
                            "center",
                        }}
                      >
                        {tr("You’ve played", "Has jugado")}{" "}
                        {fmt(
                          rew.played ||
                            0
                        )}{" "}
                        {tr("battles this week.", "batallas esta semana.")}
                      </Typography>
                    </>
                  )}
                </Box>
              ))}

              <Typography
                variant="body2"
                sx={{
                  opacity: 0.8,
                  mb: 2,
                  textAlign: "center",
                }}
              >
                {tr("Rewards are delivered by the same account used for tickets. Check your wallet / inventory.", "Las recompensas se entregan mediante la misma cuenta usada para los tickets. Revisa tu billetera o inventario.")}
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                justifyContent="center"
              >
                <HelpTooltip title={tr("Close this reward window and update your Arena information.", "Cierra esta ventana de recompensa y actualiza la información de tu Arena.")}>
                  <Button
                    {...btnPrimary}
                    onClick={async () => {
                      setRewardOpen(false);
                      await refreshAll();
                    }}
                  >
                    {tr("Great!", "¡Excelente!")}
                  </Button>
                </HelpTooltip>
                <HelpTooltip title={tr("Check the backend again for updated tickets, PP and balances.", "Consulta nuevamente el backend para actualizar tickets, PP y saldos.")}>
                  <Button
                    {...btnGhost}
                    startIcon={<RefreshRoundedIcon />}
                    onClick={() => refreshAll({ showMessage: true })}
                    disabled={refreshing}
                  >
                    {tr("Refresh Balance", "Actualizar saldo")}
                  </Button>
                </HelpTooltip>
              </Stack>
            </>
          ) : (
            <Typography>
              {tr("No reward info.", "No hay información de recompensa.")}
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={closeSnack}
      >
        <Alert
          onClose={closeSnack}
          severity={snack.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
